// 认证工具文件 - 基于新的JWT系统

const API_BASE = '/api/auth';

// 内存中存储访问JWT（页面关闭即丢失）
let visitJwt = null;

// 设置访问JWT（仅存储在内存）
export const setVisitJwt = (token) => {
  visitJwt = token;
};

// 获取访问JWT
export const getVisitJwt = () => {
  return visitJwt;
};

// 清除访问JWT
export const clearVisitJwt = () => {
  visitJwt = null;
};

// 检查访问JWT是否有效
const isVisitJwtValid = (token) => {
  if (!token) return false;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return false;
    const payload = JSON.parse(atob(parts[1]));
    return payload.exp * 1000 > Date.now();
  } catch {
    return false;
  }
};

// 获取当前用户信息（从访问JWT中解析）
export const getCurrentUser = () => {
  if (!visitJwt || !isVisitJwtValid(visitJwt)) {
    return null;
  }
  try {
    const parts = visitJwt.split('.');
    const payload = JSON.parse(atob(parts[1]));
    return {
      user_id: payload.uid,        // JWT中使用uid字段
      username: payload.sub,       // JWT中使用sub字段  
      is_admin: payload.adm || false  // JWT中使用adm字段
    };
  } catch (error) {
    console.error('[Auth] 解析用户信息失败:', error);
    return null;
  }
};

// 自动获取/刷新访问JWT
export const ensureVisitJwt = async () => {
  if (!visitJwt || !isVisitJwtValid(visitJwt)) {
    try {
      const response = await fetch(`${API_BASE}/daily-jwt-api.php`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      const result = await response.json();
      if (result.status === 'account_jwt_missing' || result.status === 'account_jwt_invalid') {
        console.warn('[Auth] 账号JWT无效，需要重新登录:', result.message);
        setVisitJwt(null);
        if (result.redirect === 'login') {
          window.dispatchEvent(new CustomEvent('auth:login-required', {
            detail: {
              reason: result.status,
              message: result.message
            }
          }));
        }
        return false;
      }
      if (result.status === 'ok' && result.visit_jwt) {
        setVisitJwt(result.visit_jwt);
        return true;
      }
      setVisitJwt(null);
      return false;
    } catch (error) {
      console.error('[Auth] 获取访问JWT失败:', error);
      setVisitJwt(null);
      return false;
    }
  }
  return true;
};

// 通用请求函数（自动携带访问JWT）
export const request = async (url, options = {}) => {
  const headers = {
    'Content-Type': 'application/json',
    ...(visitJwt && { 'Authorization': `Bearer ${visitJwt}` }),
    ...options.headers
  };
  const response = await fetch(url, {
    ...options,
    headers,
    credentials: 'include'
  });
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }
  return await response.json();
};

// 专门用于表单数据的API请求函数（支持自动JWT刷新）
export const apiRequest = async (url, options = {}) => {
  // 确保有访问JWT
  const hasJwt = await ensureVisitJwt();
  if (!hasJwt) {
    throw new Error('认证失败，请重新登录');
  }
  
  const headers = {
    ...(visitJwt && { 'Authorization': `Bearer ${visitJwt}` }),
    ...options.headers
  };
  
  const response = await fetch(url, {
    ...options,
    headers,
    credentials: 'include'
  });
  
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }
  
  const result = await response.json();
  
  // 检查是否需要重新获取JWT
  if (result.status === 'visit_jwt_invalid' || result.status === 'jwt_expired' || result.status === 'jwt_invalid' || 
      (result.action === 'refresh_token')) {
    // 尝试刷新JWT后重试
    const refreshed = await ensureVisitJwt();
    if (refreshed) {
      // 重新构建headers
      const retryHeaders = {
        ...(visitJwt && { 'Authorization': `Bearer ${visitJwt}` }),
        ...options.headers
      };
      
      const retryResponse = await fetch(url, {
        ...options,
        headers: retryHeaders,
        credentials: 'include'
      });
      
      if (!retryResponse.ok) {
        throw new Error(`HTTP ${retryResponse.status}: ${retryResponse.statusText}`);
      }
      
      return await retryResponse.json();
    } else {
      throw new Error('认证失败，请重新登录');
    }
  }
  
  return result;
};

// 登录成功后保存访问JWT
export const saveLoginTokens = (data) => {
  if (data.visit_jwt) {
    visitJwt = data.visit_jwt;
  }
};

// 账号设置API请求函数（复用apiRequest的JWT刷新逻辑）
export const accountRequest = async (action, data = {}) => {
  const formData = new FormData();
  formData.append('action', action);
  
  // 添加其他参数
  for (const [key, value] of Object.entries(data)) {
    if (value !== null && value !== undefined && value !== '') {
      formData.append(key, value);
    }
  }
  
  return await apiRequest(`${API_BASE}/account-settings-api.php`, {
    method: 'POST',
    body: formData
  });
};

// 用户管理API请求函数（复用apiRequest的JWT刷新逻辑）
export const userManagementRequest = async (action, data = {}) => {
  const formData = new FormData();
  formData.append('action', action);
  
  // 添加其他参数
  for (const [key, value] of Object.entries(data)) {
    if (value !== null && value !== undefined && value !== '') {
      formData.append(key, value);
    }
  }
  
  return await apiRequest(`${API_BASE}/user-management-api.php`, {
    method: 'POST',
    body: formData
  });
};

// 退出登录
export const logout = async () => {
  try {
    clearVisitJwt();
    await fetch(`${API_BASE}/logout.php`, {
      method: 'POST',
      credentials: 'include'
    });
    return true;
  } catch {
    return false;
  }
};

// API 调用函数（用于文件操作等）
export const apiCall = async (action, data = {}, options = {}) => {
  try {
    // 确保有访问JWT
    const hasJwt = await ensureVisitJwt();
    if (!hasJwt) {
      throw new Error('认证失败，请重新登录');
    }

    // 判断是否为上传操作（已经是FormData）
    let body;
    if (data instanceof FormData) {
      body = data;
      // 为FormData添加action和jwt
      body.append('action', action);
      const jwt = getVisitJwt();
      if (jwt) {
        body.append('jwt', jwt);
      }
    } else {
      // 普通数据，创建FormData
      body = new FormData();
      body.append('action', action);
      
      // 添加JWT访问令牌
      const jwt = getVisitJwt();
      if (jwt) {
        body.append('jwt', jwt);
      }
      
      // 添加其他参数
      for (const [key, value] of Object.entries(data)) {
        if (value !== null && value !== undefined && value !== '') {
          body.append(key, value);
        }
      }
    }

    // 构建请求配置
    const requestConfig = {
      method: 'POST',
      body,
      credentials: 'include',
      ...options
    };

    // 确定API端点 - 所有文件操作都通过 file-api.php
    const apiEndpoint = '/api/file/file-api.php';

    const response = await fetch(apiEndpoint, requestConfig);
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    const result = await response.json();
    return result;
  } catch (error) {
    console.error(`[API] ${action} 操作失败:`, error);
    throw error;
  }
};
